import { createColumnHelper } from '@tanstack/react-table'
import { useMemo } from 'react'

import { RequestLog } from '@engine/models'

import { Table } from '#components/Table'

import { GetTrackingStatusBadge } from './GetTrackingStatusBadge'

interface GetTrackingsListProps {
  trackings: RequestLog[]
  selectedId?: string
  onSelect: (tracking: RequestLog) => void
  infiniteScrollRef?: React.RefObject<HTMLDivElement>
  isFetchingMore?: boolean
}

const columnHelper = createColumnHelper<RequestLog>()

const GetTrackingsList = ({
  trackings,
  selectedId,
  onSelect,
  infiniteScrollRef,
  isFetchingMore,
}: GetTrackingsListProps) => {
  const columns = useMemo(
    () => [
      columnHelper.accessor('externalId', {
        header: 'External ID',
        cell: info => (
          <div className="truncate max-w-[200px]">
            {info.getValue() ?? (
              <span className="opacity-50">&lt;none&gt;</span>
            )}
          </div>
        ),
      }),
      columnHelper.accessor('tenantRequest.content.trackingNumber', {
        header: 'PRO #',
        cell: info => <div className="text-xs">{info.getValue()}</div>,
      }),
      columnHelper.accessor('requestId', {
        header: 'Request ID',
        cell: info => <div className="text-xs">{info.getValue()}</div>,
      }),
      columnHelper.display({
        id: 'carrier',
        header: 'Carrier',
        cell: info => {
          const carrierCode = (info.row.original.tenantResponse?.content as any)
            ?.carrierCode as string | undefined
          return carrierCode ? (
            <span className="badge badge-sm badge-info uppercase">
              {carrierCode}
            </span>
          ) : (
            <span className="text-xs opacity-50">N/A</span>
          )
        },
      }),
      columnHelper.display({
        header: 'Status',
        cell: info => <GetTrackingStatusBadge log={info.row.original} />,
      }),
      columnHelper.accessor('tenantRequest.requestDate', {
        header: 'Request Date',
        cell: info => {
          const date = info.getValue()
          return date ? (
            <span className="text-xs">{new Date(date).toLocaleString()}</span>
          ) : null
        },
      }),
      columnHelper.accessor('tenantResponse.responseTime', {
        header: 'Response Time',
        cell: info => {
          const time = info.getValue()
          if (!time) return <span className="text-xs opacity-50">N/A</span>
          const formatted =
            time < 1000 ? `${time}ms` : `${(time / 1000).toFixed(2)}s`
          const colorClass =
            time < 1000
              ? 'text-success'
              : time < 3000
                ? 'text-warning'
                : 'text-error'
          return (
            <span className={`text-xs font-semibold ${colorClass}`}>
              {formatted}
            </span>
          )
        },
      }),
    ],
    [],
  )

  return (
    <Table
      data={trackings}
      columns={columns}
      getRowId={tracking => tracking._id}
      selectedId={selectedId}
      onRowClick={onSelect}
      infiniteScrollRef={infiniteScrollRef}
      isFetchingMore={isFetchingMore}
    />
  )
}

export { GetTrackingsList }
